import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calendar, ChevronLeft, ChevronRight, Download, Printer, LogOut,
  Loader2, Users, Clock, Coffee
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

const MANAGER_ROLES = ['owner', 'mgr-sup', 'manager', 'admin'];

/**
 * Weekly Roster Page
 * Displays a printable/downloadable roster grid showing all staff schedules
 * Accessible by Owner and Mgr-Sup roles
 */
export default function WeeklyRosterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [staffInfo, setStaffInfo] = useState(null);
  const [roster, setRoster] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(now.setDate(diff));
  });
  const rosterRef = useRef(null);

  const token = localStorage.getItem('staff_token');

  useEffect(() => {
    if (!token) {
      navigate('/staff/login');
      return;
    }
    fetchStaffInfo();
  }, [token, navigate]);

  useEffect(() => {
    if (staffInfo && MANAGER_ROLES.includes(staffInfo.role?.toLowerCase())) {
      fetchRoster();
    }
  }, [staffInfo, currentWeekStart]);

  const fetchStaffInfo = async () => {
    try {
      const res = await api.get(`/staff-portal/profile?token=${token}`);
      setStaffInfo(res.data);
      if (!MANAGER_ROLES.includes(res.data.role?.toLowerCase())) {
        toast.error('Access denied. Managers only.');
        navigate('/staff/profile');
      }
    } catch (err) {
      console.error('Failed to fetch staff info:', err);
      navigate('/staff/login');
    }
  };

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const weekStartStr = currentWeekStart.toISOString().split('T')[0];
      const res = await api.get(
        `/staff-portal/weekly-roster?token=${token}&week_start=${weekStartStr}`
      );
      
      if (res.data.success) {
        setRoster(res.data.roster);
      } else {
        toast.error(res.data.detail || 'Failed to load roster');
      }
    } catch (err) {
      console.error('Failed to fetch roster:', err);
      toast.error('Failed to load roster');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_info');
    toast.success('Logged out');
    navigate('/staff/login');
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    // Using browser print to PDF functionality
    window.print();
    toast.success('Use "Save as PDF" in the print dialog to download');
  };

  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  if (loading && !roster) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #roster-printable, #roster-printable * {
            visibility: visible;
          }
          #roster-printable {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 20px;
            background: white !important;
            color: black !important;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          @page {
            size: landscape;
            margin: 0.5cm;
          }
          .roster-table {
            font-size: 11px;
          }
          .roster-table th, .roster-table td {
            padding: 6px 4px !important;
            border: 1px solid #333 !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* Header - No Print */}
        <header className="no-print bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/staff/profile" className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                <ChevronLeft className="w-6 h-6" />
              </Link>
              <div>
                <h1 className="font-bold text-gray-900 dark:text-white">Weekly Roster</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">{staffInfo?.name} - {staffInfo?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-roster-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPDF} data-testid="download-roster-btn">
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Week Navigation - No Print */}
        <div className="no-print bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous Week
            </Button>
            <div className="text-center">
              <p className="font-semibold text-gray-900 dark:text-white text-lg">
                {roster?.week_display || 'Loading...'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="next-week-btn">
              Next Week
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : roster ? (
            <div id="roster-printable" ref={rosterRef}>
              {/* Print Header */}
              <div className="print-only mb-6 text-center">
                <h1 className="text-2xl font-bold text-gray-900">Bulaccino Café - Weekly Staff Roster</h1>
                <p className="text-gray-600">{roster.week_display}</p>
                <p className="text-sm text-gray-500 mt-1">Opening Hours: {roster.cafe_info?.hours} ({roster.cafe_info?.days})</p>
              </div>

              {/* Summary Stats - Screen Only */}
              <div className="no-print grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{roster.summary?.staff_with_shifts || 0}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Staff Scheduled</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{roster.summary?.total_shifts || 0}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Shifts</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">{roster.summary?.total_hours || 0}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total Hours</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center">
                      <Coffee className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900 dark:text-white">{roster.cafe_info?.shift_time?.split(' ')[0]}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Shift Time</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Roster Table */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="roster-table w-full text-sm" data-testid="roster-table">
                    <thead>
                      <tr className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                        <th className="text-left py-3 px-4 font-semibold border-r border-teal-500 sticky left-0 bg-gradient-to-r from-teal-600 to-teal-700 z-10">
                          Day
                        </th>
                        {roster.staff_columns?.map((staff) => (
                          <th 
                            key={staff.staff_id} 
                            className="text-center py-3 px-3 font-semibold border-r border-teal-500 min-w-[100px]"
                          >
                            <div className="text-white font-bold">{staff.staff_first_name}</div>
                            <div className="text-teal-200 text-xs font-normal capitalize">{staff.role?.replace('mgr-sup', 'Supervisor')}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {roster.days?.map((day, dayIndex) => {
                        const isWeekend = day.is_weekend;
                        const isToday = day.date === new Date().toISOString().split('T')[0];
                        
                        return (
                          <tr 
                            key={day.date}
                            className={`
                              ${isWeekend ? 'bg-amber-50 dark:bg-amber-900/10' : 'bg-white dark:bg-gray-800'}
                              ${isToday ? 'ring-2 ring-inset ring-teal-500' : ''}
                              hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors
                            `}
                          >
                            <td className={`
                              py-3 px-4 border-r border-gray-200 dark:border-gray-700 sticky left-0 z-10
                              ${isWeekend ? 'bg-amber-50 dark:bg-amber-900/20 font-semibold' : 'bg-white dark:bg-gray-800'}
                              ${isToday ? 'bg-teal-50 dark:bg-teal-900/20' : ''}
                            `}>
                              <div className="flex items-center gap-2">
                                <div className={`
                                  w-10 h-10 rounded-lg flex flex-col items-center justify-center text-xs
                                  ${isToday ? 'bg-teal-600 text-white' : isWeekend ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}
                                `}>
                                  <span className="font-bold text-sm">{day.day_num}</span>
                                  <span className="text-[10px]">{day.month_short}</span>
                                </div>
                                <div>
                                  <p className={`font-medium ${isToday ? 'text-teal-700 dark:text-teal-300' : 'text-gray-900 dark:text-white'}`}>
                                    {day.day_short}
                                  </p>
                                  {isToday && (
                                    <span className="text-[10px] bg-teal-100 dark:bg-teal-900/50 text-teal-700 dark:text-teal-300 px-1.5 rounded">TODAY</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            {roster.staff_columns?.map((staff) => {
                              const shift = staff.shifts[dayIndex];
                              const isWorking = shift?.working;
                              
                              return (
                                <td 
                                  key={`${staff.staff_id}-${day.date}`}
                                  className={`
                                    text-center py-2 px-2 border-r border-gray-200 dark:border-gray-700
                                    ${isWorking 
                                      ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300' 
                                      : 'text-gray-400 dark:text-gray-500'
                                    }
                                  `}
                                >
                                  {isWorking ? (
                                    <div>
                                      <p className="font-semibold text-green-700 dark:text-green-300 text-xs">
                                        {formatTime12h(shift.start_time)}
                                      </p>
                                      <p className="text-[10px] text-green-600 dark:text-green-400">to</p>
                                      <p className="font-semibold text-green-700 dark:text-green-300 text-xs">
                                        {formatTime12h(shift.end_time)}
                                      </p>
                                      <p className="text-[10px] text-green-600 dark:text-green-400 mt-0.5">
                                        ({shift.hours}h)
                                      </p>
                                    </div>
                                  ) : (
                                    <span className="font-bold text-gray-400 dark:text-gray-500">OFF</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {/* Total Hours Row */}
                      <tr className="bg-gray-100 dark:bg-gray-700 font-semibold">
                        <td className="py-3 px-4 border-r border-gray-200 dark:border-gray-600 sticky left-0 bg-gray-100 dark:bg-gray-700 z-10">
                          <span className="text-gray-700 dark:text-gray-300">TOTAL HOURS</span>
                        </td>
                        {roster.staff_columns?.map((staff) => (
                          <td 
                            key={`total-${staff.staff_id}`}
                            className="text-center py-3 px-2 border-r border-gray-200 dark:border-gray-600"
                          >
                            <span className="text-lg font-bold text-teal-600 dark:text-teal-400">
                              {staff.total_hours}h
                            </span>
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer Info */}
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{roster.cafe_info?.name}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Opening Hours: {roster.cafe_info?.hours} ({roster.cafe_info?.days})
                    </p>
                  </div>
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <p>Generated: {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</p>
                  </div>
                </div>
              </div>

              {/* Legend - Print Only */}
              <div className="print-only mt-4 text-center text-xs text-gray-600">
                <p className="font-semibold">Shift Time: {roster.cafe_info?.shift_time}</p>
                <p className="mt-1">OFF = Day Off | Times shown in 12-hour format</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No roster data available</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
